import React, { useEffect } from 'react';
import LoginScreen from './components/Auth/LoginScreen';
import Header from './components/Layout/Header';
import AdminDashboard from './components/Admin/AdminDashboard';
import MarketingSite from './components/Marketing/MarketingSite';
import PlansPage from './components/Marketing/PlansPage';
import PrivacyPage from './components/Marketing/PrivacyPage';
import SupportPage from './components/Marketing/SupportPage';
import UseCasePage, { useCasePages } from './components/Marketing/UseCasePage';
import useAuth from './hooks/useAuth';
import { trackWebsiteEvent } from './services/siteAnalytics';
import './styles/globals.css';
import './styles/brand.css';
import './styles/dashboard.css';
import './styles/founder.css';
import './styles/notifications.css';

export default function App() {
  const { user, isAdmin, loading, error, signIn, signOut, refreshAccess } = useAuth();
  const path = window.location.pathname.replace(/\/+$/, '') || '/';

  useEffect(() => {
    trackWebsiteEvent(path === '/privacy' ? 'privacy_viewed' : 'page_view');
  }, [path]);

  useEffect(() => {
    if (!user || path === '/internal' || path === '/operations') return;
    const key = `leavenow_auth_tracked_${user.uid}`;
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
    trackWebsiteEvent('sign_in_completed');
  }, [path, user]);

  if (loading) return <div className="app-loader" aria-label="Loading LeaveNOW"><span /></div>;

  if (path === '/operations') {
    if (!user) {
      window.location.replace('/internal');
      return <div className="app-loader" aria-label="Opening secure sign in"><span /></div>;
    }
    if (!isAdmin) return <AccessDenied user={user} error={error} onRefresh={refreshAccess} onSignOut={signOut} />;
    return <><Header user={user} isAdmin onAdmin={() => { window.location.href = '/'; }} onSignOut={signOut} /><AdminDashboard onClose={() => { window.location.href = '/'; }} /></>;
  }

  if (path === '/internal') {
    if (!user) return <LoginScreen onSignIn={signIn} error={error} founder />;
    if (!isAdmin) return <AccessDenied user={user} error={error} onRefresh={refreshAccess} onSignOut={signOut} />;
    window.location.replace('/operations');
    return <div className="app-loader" aria-label="Opening operations"><span /></div>;
  }

  if (path === '/plans') return <PlansPage user={user} onSignIn={signIn} onSignOut={signOut} authError={error} />;
  if (path === '/privacy') return <PrivacyPage />;
  if (path === '/support') return <SupportPage />;
  if (useCasePages[path]) return <UseCasePage page={useCasePages[path]} />;

  if (path === '/app' || path === '/login' || path === '/founder' || path === '/admin') {
    window.location.replace('/plans');
    return null;
  }

  return <MarketingSite />;
}

function AccessDenied({ user, error, onRefresh, onSignOut }) {
  return (
    <main className="access-denied">
      <div>
        <p className="eyebrow">Restricted access</p>
        <h1>This account is not authorized.</h1>
        <p>{user.email} is signed in, but does not have the server-issued founder role.</p>
        {error && <p className="auth-error" role="alert">{error}</p>}
        <button className="primary-button" onClick={onRefresh}>Refresh founder access</button>
        <button className="primary-button" onClick={onSignOut}>Sign in with another account</button>
        <a href="/">Return to LeaveNOW</a>
      </div>
    </main>
  );
}
